import { useThemeContext, useAppContext } from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { Component, Index, Show, createMemo } from 'solid-js';
import { CustomInput } from '@tcweb/connect-ui';
import { ThemeSelector } from '@trimble/page-header';
import { ExperimentToggle } from './ExperimentToggle';

export const DebugSettings: Component = () => {
  const { web3dSettings, setWeb3dSettings, setTheme } = useThemeContext();
  const { experiments, envName, deployedTag } = useAppContext();
  const experimentEntries = createMemo(() => Object.entries(experiments()));

  function onClippingDistanceInput(e: Event): void {
    const clippingDistance = Number((e.target as HTMLInputElement).value);

    if (clippingDistance >= 0) {
      const settings = web3dSettings();
      settings.imageViewPointcloudClippingDistance = clippingDistance;

      setWeb3dSettings({
        ...settings,
      });
    }
  }

  function onPreRenderStationCountInput(e: Event): void {
    const stationCount = Number((e.target as HTMLInputElement).value);

    if (stationCount >= 0) {
      const settings = web3dSettings();
      settings.imageViewPreRenderStationCount = stationCount;

      setWeb3dSettings({
        ...settings,
      });
    }
  }

  function clearLocalStorageAndRefresh(): void {
    if (
      confirm(
        'This will clear all localStorage data and refresh the page. Are you sure?'
      )
    ) {
      localStorage.clear();
      window.location.reload();
    }
  }

  return (
    <div data-testid="debug-settings" class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <span class="text-sm">Environment</span>
        <span class="text-sm text-gray-5">{envName}</span>
      </div>

      {deployedTag && (
        <div class="flex items-center justify-between">
          <span class="text-sm">Deployed Tag</span>
          <span class="text-sm text-gray-5">{deployedTag}</span>
        </div>
      )}

      <div class="flex items-center justify-between">
        <span class="text-sm">Theme</span>
        <ThemeSelector onThemeChange={setTheme} />
      </div>

      <CustomInput
        name="clipping-distance"
        label={t(
          'mobileMapping.debugPanel.imageViewPointcloudClippingDistance.label'
        )}
        type="number"
        value={web3dSettings().imageViewPointcloudClippingDistance.toString()}
        onInput={onClippingDistanceInput}
        placeholder={t(
          'mobileMapping.debugPanel.imageViewPointcloudClippingDistance.placeholder'
        )}
      />

      <CustomInput
        name="pre-render-station-count"
        label={t(
          'mobileMapping.debugPanel.imageViewPreRenderStationCount.label'
        )}
        type="number"
        value={web3dSettings().imageViewPreRenderStationCount.toString()}
        onInput={onPreRenderStationCountInput}
        placeholder={t(
          'mobileMapping.debugPanel.imageViewPreRenderStationCount.placeholder'
        )}
      />

      <Show when={experimentEntries().length > 0}>
        <div class="flex-col items-center justify-between">
          <span class="text-sm font-bold">Experiments</span>
          <Index each={experimentEntries()}>
            {(item) => <ExperimentToggle name={item()[0]} />}
          </Index>
        </div>
      </Show>

      <div class="flex items-center justify-between">
        <span class="text-sm">Local Storage</span>
        <modus-wc-button
          aria-label="Clear local storage and refresh"
          color="danger"
          shape="rectangle"
          type="button"
          variant="filled"
          onClick={clearLocalStorageAndRefresh}
        >
          Clear & Refresh
        </modus-wc-button>
      </div>
    </div>
  );
};
